import { Link, useNavigate } from "react-router-dom";
import { EmptyState } from "@/components/EmptyState";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Users, Search, Briefcase, AlertTriangle, DollarSign,
  ArrowUpRight, ArrowUpDown, ArrowUp, ArrowDown, Download,
} from "lucide-react";
import { useEffect, useState } from "react";
import { QuickViews, QuickView } from "@/components/QuickViews";
import { useCustomersServerPaging } from "@/hooks/useCustomersServerPaging";

type SortField = "jobCount" | "totalValue" | "openIssueCount" | "lastActivity";
type SortDir = "asc" | "desc";

function SortIcon({ field, activeField, dir }: { field: SortField; activeField: SortField | undefined; dir: SortDir }) {
  if (activeField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
  return dir === "asc"
    ? <ArrowUp className="h-3 w-3 ml-1 text-primary" />
    : <ArrowDown className="h-3 w-3 ml-1 text-primary" />;
}

export default function CustomersListServer() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [activeView, setActiveView] = useState<"all" | "active" | "issues" | "recent" | "completed">("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [regionFilter, setRegionFilter] = useState("all");
  const [sortField, setSortField] = useState<SortField | undefined>();
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const data = useCustomersServerPaging({
    enabled: true,
    page,
    pageSize: PAGE_SIZE,
    search,
    view: activeView,
    city: cityFilter,
    branch: branchFilter,
    region: regionFilter,
    sortField,
    sortDir,
  });

  const summary = data.summary;
  const totalPages = Math.max(1, Math.ceil(data.count / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    if (safePage !== page) setPage(safePage);
  }, [safePage, page]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((value) => (value === "desc" ? "asc" : "desc"));
      return;
    }
    setSortField(field);
    setSortDir("desc");
  };

  const quickViews: QuickView[] = [
    { key: "all", label: "All Customers", count: summary?.totalCustomers ?? 0 },
    { key: "active", label: "Active Jobs", count: summary?.withOpenJobs ?? 0, accent: "info" },
    { key: "issues", label: "Open Issues", count: summary?.withIssues ?? 0, accent: "destructive" },
    { key: "recent", label: "Recent Activity", count: summary?.recentActivity ?? 0, accent: "success" },
    { key: "completed", label: "Completed Only", count: summary?.completedOnly ?? 0 },
  ];

  const exportCsv = () => {
    const headers = ["Name", "Phone", "Email", "Address", "City", "Branch", "Region", "Jobs", "Open Jobs", "Open Issues", "Total Value", "Last Activity"];
    const csv = [headers, ...data.rows.map((row) => [
      row.name,
      row.phone,
      row.email,
      row.address,
      row.city,
      row.branch,
      row.region,
      row.jobCount,
      row.openJobCount,
      row.openIssueCount,
      row.totalValue,
      row.lastActivity,
    ])].map((record) => record.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold tracking-tight flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Customers
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {summary?.totalCustomers ?? 0} customers · {summary?.totalOpenJobs ?? 0} open jobs · ${(summary?.totalValue ?? 0).toLocaleString()} total value
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5" onClick={exportCsv}>
            <Download className="h-3.5 w-3.5" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total Customers", value: summary?.totalCustomers ?? 0, icon: Users },
          { label: "Open Jobs", value: summary?.totalOpenJobs ?? 0, icon: Briefcase },
          { label: "Open Issues", value: summary?.totalIssues ?? 0, icon: AlertTriangle },
          { label: "Total Value", value: `$${Math.round(summary?.totalValue ?? 0).toLocaleString()}`, icon: DollarSign },
        ].map((kpi) => (
          <Card key={kpi.label} className="shadow-none">
            <CardContent className="p-3 text-center">
              <kpi.icon className="h-4 w-4 text-muted-foreground mx-auto mb-1" />
              <p className="text-lg font-bold">{kpi.value}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <QuickViews views={quickViews} active={activeView} onChange={(value) => { setActiveView(value as typeof activeView); setPage(1); }} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search name, phone, email, address..."
            value={search}
            onChange={(event) => { setSearch(event.target.value); setPage(1); }}
            className="pl-9 h-9 text-xs"
          />
        </div>
        <Select value={cityFilter} onValueChange={(value) => { setCityFilter(value); setPage(1); }}>
          <SelectTrigger className="h-9 text-xs w-[140px]">
            <SelectValue placeholder="City" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cities</SelectItem>
            {(summary?.cities ?? []).map((city) => (
              <SelectItem key={city} value={city}>{city}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={branchFilter} onValueChange={(value) => { setBranchFilter(value); setPage(1); }}>
          <SelectTrigger className="h-9 text-xs w-[140px]">
            <SelectValue placeholder="Branch" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Branches</SelectItem>
            {(summary?.branches ?? []).map((branch) => (
              <SelectItem key={branch} value={branch}>{branch}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={regionFilter} onValueChange={(value) => { setRegionFilter(value); setPage(1); }}>
          <SelectTrigger className="h-9 text-xs w-[140px]">
            <SelectValue placeholder="Region" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Regions</SelectItem>
            {(summary?.regions ?? []).map((region) => (
              <SelectItem key={region} value={region}>{region}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {(activeView !== "all" || cityFilter !== "all" || branchFilter !== "all" || regionFilter !== "all" || search) && (
        <p className="text-[11px] text-muted-foreground">
          Showing {data.count} customers in this view
        </p>
      )}

      <Card className="shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[11px] font-semibold">Customer</TableHead>
                <TableHead className="text-[11px] font-semibold hidden sm:table-cell">Phone</TableHead>
                <TableHead className="text-[11px] font-semibold hidden lg:table-cell">Email</TableHead>
                <TableHead className="text-[11px] font-semibold hidden md:table-cell">City / Area</TableHead>
                <TableHead className="text-[11px] font-semibold text-center cursor-pointer select-none hover:text-primary transition-colors" onClick={() => toggleSort("jobCount")}>
                  <span className="inline-flex items-center">Jobs <SortIcon field="jobCount" activeField={sortField} dir={sortDir} /></span>
                </TableHead>
                <TableHead className="text-[11px] font-semibold text-center hidden sm:table-cell">Open</TableHead>
                <TableHead className="text-[11px] font-semibold text-center hidden sm:table-cell cursor-pointer select-none hover:text-primary transition-colors" onClick={() => toggleSort("openIssueCount")}>
                  <span className="inline-flex items-center">Issues <SortIcon field="openIssueCount" activeField={sortField} dir={sortDir} /></span>
                </TableHead>
                <TableHead className="text-[11px] font-semibold text-right hidden md:table-cell cursor-pointer select-none hover:text-primary transition-colors" onClick={() => toggleSort("totalValue")}>
                  <span className="inline-flex items-center justify-end">Value <SortIcon field="totalValue" activeField={sortField} dir={sortDir} /></span>
                </TableHead>
                <TableHead className="text-[11px] font-semibold text-right hidden lg:table-cell cursor-pointer select-none hover:text-primary transition-colors" onClick={() => toggleSort("lastActivity")}>
                  <span className="inline-flex items-center justify-end">Last Activity <SortIcon field="lastActivity" activeField={sortField} dir={sortDir} /></span>
                </TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((customer) => (
                <TableRow key={customer.customerId || customer.name} className="hover:bg-muted/30">
                  <TableCell className="py-3">
                    <div className="space-y-1">
                      <Link to={customer.customerId ? `/customers/${customer.customerId}` : `/customers/${encodeURIComponent(customer.name)}`} className="font-semibold text-xs text-foreground hover:text-primary transition-colors">
                        {customer.name}
                      </Link>
                      <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                        {customer.openIssueCount > 0 && (
                          <Badge variant="outline" className="h-5 px-1.5 text-[10px] border-destructive/20 text-destructive">
                            {customer.openIssueCount} issue{customer.openIssueCount === 1 ? "" : "s"}
                          </Badge>
                        )}
                        {customer.address && <span>{customer.address}</span>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-xs">{customer.phone || "—"}</TableCell>
                  <TableCell className="hidden lg:table-cell text-xs">{customer.email || "—"}</TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="text-xs">
                      <div className="font-medium">{customer.city || "—"}</div>
                      <div className="text-muted-foreground">{customer.branch || customer.region || "—"}</div>
                    </div>
                  </TableCell>
                  <TableCell className="text-center text-xs font-medium">{customer.jobCount}</TableCell>
                  <TableCell className="text-center hidden sm:table-cell text-xs">{customer.openJobCount}</TableCell>
                  <TableCell className="text-center hidden sm:table-cell text-xs">
                    {customer.openIssueCount > 0 ? <span className="text-destructive font-medium">{customer.openIssueCount}</span> : "—"}
                  </TableCell>
                  <TableCell className="text-right hidden md:table-cell text-xs">${customer.totalValue.toLocaleString()}</TableCell>
                  <TableCell className="text-right hidden lg:table-cell text-xs text-muted-foreground">{customer.lastActivity.slice(0, 10)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                      <Link to={customer.customerId ? `/customers/${customer.customerId}` : `/customers/${encodeURIComponent(customer.name)}`}>
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {data.loading && data.rows.length === 0 ? (
            <div className="p-8">
              <EmptyState title="Loading customers" description="Fetching customer records..." icon={<Users className="h-4 w-4" />} />
            </div>
          ) : data.rows.length === 0 ? (
            <div className="p-8">
              <button type="button" onClick={() => navigate("/opportunities")} className="w-full text-left">
                <EmptyState title="No customers yet" description="When jobs are created, customer records will appear here." icon={<Users className="h-4 w-4" />} />
              </button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {data.count > 0 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-muted-foreground">
            Showing {(safePage - 1) * PAGE_SIZE + 1}-{Math.min(safePage * PAGE_SIZE, data.count)} of {data.count}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-8 text-xs" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>Previous</Button>
            <span className="text-xs text-muted-foreground">{safePage} / {totalPages}</span>
            <Button variant="outline" size="sm" className="h-8 text-xs" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
